import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import {
  makeStyles, tokens, Spinner, Badge, Button,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Field, Input, Textarea,
  
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '../i18n';
import { useTenantStore } from '../store/tenant.store';
import {
  fetchVocabulary, fetchPendingVocabulary, addVocabularyTerm,
  approveVocabularyTerm, rejectVocabularyTerm, deleteVocabularyTerm,
  type AddVocabularyInput,
} from '../api/vocabulary.api';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  table:   { width: '100%', overflowX: 'auto' as const },
  th:      { fontWeight: tokens.fontWeightSemibold, padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  td:      { padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
});

export function VocabularyPage() {
  const s = useStyles();
  const t = useT();
  const { displayName } = useTenantStore();
  const vocabularyTitle = displayName
    ? t.vocabulary.titleWithCompany.replace('{company}', displayName)
    : t.vocabulary.title;
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<Partial<AddVocabularyInput> & { aliasesText?: string }>({});
  const fe = useFieldErrors();

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['vocabulary'],
    queryFn:  fetchVocabulary,
  });

  const { data: pending = [] } = useQuery({
    queryKey: ['vocabulary-pending'],
    queryFn:  fetchPendingVocabulary,
  });

  const addMutation = useMutation({
    mutationFn: addVocabularyTerm,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vocabulary'] });
      setDialogOpen(false);
      setForm({});
    },
  });

  const approveMutation = useMutation({
    mutationFn: approveVocabularyTerm,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vocabulary'] });
      qc.invalidateQueries({ queryKey: ['vocabulary-pending'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: rejectVocabularyTerm,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vocabulary-pending'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteVocabularyTerm,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vocabulary'] }),
  });

  return (
    <div className={s.root}>
      <PageTitle>{vocabularyTitle}</PageTitle>
      <p style={{ margin: 0, color: tokens.colorNeutralForeground3 }}>
        {t.vocabulary.description}
      </p>

      {/* Sugerencias pendientes (auto-aprendizaje) */}
      {pending.length > 0 && (
        <div>
          <strong>{t.vocabulary.pendingTitle}</strong>
          <DataTable<(typeof pending)[number]>
            rows={pending}
            rowKey={(p) => p.scopeKey}
            columns={[
              { key: 'aliases', header: t.vocabulary.aliasDetected, render: (p) => p.aliases.join(', ') },
              { key: 'term', header: t.vocabulary.termSuggested },
              { key: 'origin', header: t.vocabulary.origin, render: (p) => <Badge appearance="tint">{p.scope === 'USER' ? t.vocabulary.user : t.vocabulary.tenant}</Badge> },
              { key: 'actions', header: t.vocabulary.actions, render: (p) => (
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                  <Button size="small" appearance="primary" disabled={approveMutation.isPending} onClick={() => approveMutation.mutate(p.scopeKey)}>{t.vocabulary.approve}</Button>
                  <Button size="small" appearance="secondary" disabled={rejectMutation.isPending} onClick={() => rejectMutation.mutate(p.scopeKey)}>{t.vocabulary.reject}</Button>
                </div>
              ) },
            ]}
          />
        </div>
      )}

      {/* Términos activos */}
      <div className={s.toolbar}>
        <strong>{t.vocabulary.activeTerms}</strong>
        <Button appearance="primary" onClick={() => setDialogOpen(true)}>{t.vocabulary.addTerm}</Button>
      </div>

      {isLoading && <Spinner size="small" label={t.common.loading} />}

      <DataTable<(typeof items)[number]>
        rows={isLoading ? [] : items}
        rowKey={(item) => item.scopeKey}
        emptyMessage={isLoading ? '…' : t.vocabulary.noTerms}
        columns={[
          { key: 'term', header: t.vocabulary.term },
          { key: 'aliases', header: t.vocabulary.aliases, render: (item) => item.aliases.join(', ') },
          { key: 'category', header: t.vocabulary.category },
          { key: 'scope', header: t.vocabulary.scope, render: (item) => <Badge appearance="tint">{item.scope === 'USER' ? t.vocabulary.user : t.vocabulary.tenant}</Badge> },
          { key: 'origin', header: t.vocabulary.origin, render: (item) => (item.source === 'AUTO_LEARNED' ? t.vocabulary.autoLearned : t.vocabulary.manual) },
          { key: 'actions', header: t.vocabulary.actions, render: (item) => (
            <Button size="small" appearance="secondary" onClick={() => deleteMutation.mutate(item.scopeKey)}>{t.vocabulary.delete}</Button>
          ) },
        ]}
      />

      {/* Dialog agregar término */}
      <Dialog open={dialogOpen} onOpenChange={(_, d) => { setDialogOpen(d.open); fe.clear(); }}>
        <DialogSurface>
          <DialogTitle>{t.vocabulary.dialogTitle}</DialogTitle>
          <DialogBody>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Field label={t.vocabulary.canonicalTerm} required {...fe.props('term')}>
                <Input
                  value={form.term ?? ''}
                  onChange={(_, d) => { setForm(f => ({ ...f, term: d.value })); fe.clear('term'); }}
                  placeholder={t.vocabulary.placeCanonical}
                />
              </Field>
              <Field label={t.vocabulary.aliasesComma} required {...fe.props('aliasesText')}>
                <Textarea
                  value={form.aliasesText ?? ''}
                  onChange={(_, d) => { setForm(f => ({ ...f, aliasesText: d.value })); fe.clear('aliasesText'); }}
                  placeholder={t.vocabulary.placeAliases}
                />
              </Field>
              <Field label={t.vocabulary.category} required {...fe.props('category')}>
                <Input
                  value={form.category ?? ''}
                  onChange={(_, d) => { setForm(f => ({ ...f, category: d.value })); fe.clear('category'); }}
                  placeholder={t.vocabulary.placeCategory}
                />
              </Field>
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => { setDialogOpen(false); fe.clear(); }}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              disabled={addMutation.isPending}
              onClick={() => {
                const req = t.common.validation.fieldRequired;
                const ok = fe.validate({
                  term: !form.term?.trim() && req,
                  aliasesText: !form.aliasesText?.trim() && req,
                  category: !form.category?.trim() && req,
                });
                if (!ok) return;
                const aliases = (form.aliasesText ?? '')
                  .split(',')
                  .map(a => a.trim())
                  .filter(Boolean);
                addMutation.mutate({ term: form.term!, aliases, category: form.category! });
              }}
            >
              {t.common.save}
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
